<script setup lang="ts">
import { useStorage } from '@vueuse/core';
import type { PropertyOnPage } from '#shared/property/api-types';
import type { IadAppRoute } from '~/application';
import type { ContactForm } from '#shared/contact/types';

const props = defineProps<{
  property: PropertyOnPage
}>();

const isFormOpen = defineModel<boolean>({ default: false });
function openForm() {
  isFormOpen.value = true;
}

const { t } = useI18n();
const agent = toRef(() => props.property.agent);
const title = computed(() => {
  if (!props.property) return t(`property.contact_agent.title.male`);
  if (agent.value.displayGender === 'female') return t(`property.contact_agent.title.female`);
  return t(`property.contact_agent.title.male`);
});

const agentPageLink = computed<IadAppRoute<`real-estate-agent-userName`>>(() => ({
  name: `real-estate-agent-userName`,
  params: { userName: agent.value.userName },
}));
const preventPhoneNavigationTrigger = () => {};

const isMounted = useMounted();
const phoneLink = computed(() => {
  if (!props.property) return ``;
  if (!isMounted.value) return ``;
  return `tel:${atob(props.property.agent.hashedPhone)}`;
});

const route = useRoute();
const returnTo = useStorage(`agent-${props.property.agent.userName}`, route.fullPath);
returnTo.value = route.fullPath;
watch(() => route.fullPath, () => {
  returnTo.value = route.fullPath;
}, { immediate: true });
const { notifySuccess, notifyError } = useNotifications();

const propertyListingRef = computed(() => props.property.propertyListingRef);
const { data: contactFormResponse, isPending: loading, error, mutate } = usePropertyContactForm(propertyListingRef);

watch(contactFormResponse, (contactFormResponse) => {
  if (!contactFormResponse) return;
  notifySuccess(t('core.form.message_sent'));
});

watch(error, (error) => {
  if (!error) return;
  notifyError(t('core.form.error.enter_mandatory_fields'));
});

function trackNavigation() {
  trackEvent({
    event: 'agent_interaction',
    event_name: 'agent_profile_click',
    agent_id: props.property.agent.localId,
    property_id: props.property.propertyListingRef,
    property_category: getTrackingPropertyCategory(props.property),
  });
}

function onSubmit(form: ContactForm) {
  trackEvent({
    event: 'generate_lead',
    lead_type: `agent_form`,
    agent_id: props.property.agent.localId,
    property_id: props.property.propertyListingRef,
    property_category: getTrackingPropertyCategory(props.property),
  });
  mutate(form);
}
</script>

<template>
  <aside class="bg-secondary rounded-t-2xl lg-rounded-b-2xl px-m py-2xl lg:py-m -mx-m lg:mx-0">
    <IadTitle
      class="text-white pb-xs"
      :text="title"
      tag="p"
    />
    <div class="bg-white rounded-lg lg:sticky top-[80px] w-80 mx-auto lg:w-auto overflow-clip">
      <NuxtLinkLocale
        :to="agentPageLink"
        class="flex flex-col lg:flex-row lg:gap-m lg:pb-m"
        @click="trackNavigation"
      >
        <AgentAvatarLarge
          :agent
          variant="property-page"
        />
        <div class="pt-m px-m lg:ps-0 flex-1 flex flex-col">
          <IadTitle
            class="text-secondary"
            variant="small-semibold"
            tag="p"
            :text="agent.fullName"
          />
          <IadReviewSummary
            v-if="agent.reviewsCount"
            class="mt-xs lg:mt-2xs"
            :average-rating="agent.reviewsRatingAverage"
            :reviews-count="agent.reviewsCount"
          />
          <AgentLanguages
            :agent
            class="mt-s lg:mt-2xs"
          />
          <div
            class="hidden lg:block mt-auto pt-m bg-white text-end"
            @click.prevent="preventPhoneNavigationTrigger"
          >
            <AgentBtnPhone
              :agent
              :property
            />
          </div>
        </div>
      </NuxtLinkLocale>
      <div
        class="grid lg:hidden grid-cols-2 gap-m pt-s pb-m px-m"
      >
        <IadBtn @click="openForm">
          <Icon name="iad-icon:mail" />
          {{ $t('agent.navigation.contact_cta_short') }}
        </IadBtn>
        <IadBtn :href="phoneLink">
          <Icon name="iad-icon:phone" />
          {{ $t(`agent.navigation.call`) }}
        </IadBtn>
      </div>
      <div class="hidden lg:flex lg:flex-col px-m pb-m gap-xs">
        <AgentContactForm
          id="fixed-contact-form"
          variant="property"
          :property
          @update:form="onSubmit"
        />
        <IadBtn
          :text="$t(`agent.form.send_message`)"
          class="w-full"
          form="fixed-contact-form"
          type="submit"
          :loading
          :disabled="loading"
        />
        <AgentDialogLegalMention />
      </div>
    </div>
  </aside>
</template>
